
import { LegalPageLayout } from "@/components/layout/LegalPageLayout";
import { useTranslation } from "react-i18next";

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation("privacy");
  const isDanish = i18n.language === "da";

  return (
    <LegalPageLayout
      title={t("title")}
      lastUpdated={t("lastUpdated")}
      hideAssistanceSheet={true}
      bgClass="bg-gradient-to-br from-purple-200 via-purple-100 to-indigo-200"
    >
      <div className="prose max-w-none">
        {isDanish ? (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-black mb-6">Privatlivspolitik</h1>
            
            <div className="mb-8 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
              <p className="text-gray-700">
                Offertio behandler dine personlige oplysninger med respekt og omhu inden for gældende ret. Derfor har vi en udvidet privatlivspolitik ved oprettelse af brugere. Hvis du gennemfører et køb og oprettes som bruger på vores platform, vil du automatisk modtage en særskilt, udvidet privatlivspolitik, der beskriver alle aspekter af databehandlingen i forbindelse med din brugerprofil, herunder lagring af købshistorik, login-oplysninger, brug af cookies, direkte markedsføring m.m. Denne politik vil blive tilsendt dig via e-mail, så snart din brugerprofil er oprettet.
              </p>
            </div>
            
            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">1. Dataansvarlig</h2>
              <p className="text-gray-700">
                Offertio<br />
                CVR-nr.: 43469754<br />
                E-mail: <a href="mailto:info@offertio.dk" className="text-primary hover:text-primary-600">info@offertio.dk</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">2. Formål og omfang</h2>
              <p className="text-gray-700">
                Denne privatlivspolitik beskriver udelukkende, hvordan Offertio behandler personoplysninger, 
                når du udfylder og indsender vores kontaktformular på hjemmesiden.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">3. Behandlede personoplysninger</h2>
              <p className="text-gray-700">Når du benytter kontaktformularen, indsamler vi følgende oplysninger:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Navn</li>
                <li>E-mailadresse</li>
                <li>Telefonnummer (valgfrit)</li>
                <li>Eventuelle øvrige oplysninger, du selv indtaster i beskedfeltet</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">4. Retligt grundlag</h2>
              <p className="text-gray-700">
                Behandlingen af dine oplysninger sker på baggrund af dit samtykke (art. 6, stk. 1, litra a, GDPR), 
                idet du aktivt indtaster og afsender dine oplysninger via kontaktformularen.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">5. Anvendelse af oplysningerne</h2>
              <p className="text-gray-700">
                Vi anvender dine oplysninger til at kunne behandle og besvare din henvendelse samt til at sende 
                relevant information eller materiale, som du har anmodet om.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">6. Videregivelse til tredjepart</h2>
              <p className="text-gray-700">
                Vi videregiver ikke dine oplysninger til andre, medmindre det er nødvendigt for at besvare din 
                henvendelse (fx ved at involvere en ekstern samarbejdspartner) eller hvis lovgivningen kræver det.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">7. Opbevaringsperiode</h2>
              <p className="text-gray-700">
                Vi opbevarer dine oplysninger i op til 6 måneder fra datoen for modtagelsen af henvendelsen, 
                medmindre der er behov for længere opbevaring for at opfylde en juridisk forpligtelse.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">8. Dine rettigheder</h2>
              <p className="text-gray-700">Du har til enhver tid ret til at:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Anmode om indsigt i, hvilke personoplysninger vi behandler om dig</li>
                <li>Anmode om berigtigelse eller sletning af dine oplysninger</li>
                <li>Begrænse behandlingen</li>
                <li>Gøre indsigelse mod behandlingen</li>
                <li>Anmode om dataportabilitet</li>
                <li>Tilbagetrække dit samtykke (uden at det påvirker lovligheden af behandlingen før tilbagetrækningen)</li>
              </ul>
              <p className="mt-4 text-gray-700">
                Hvis du ønsker at gøre brug af dine rettigheder, kontakt os venligst på 
                <a href="mailto:info@offertio.dk" className="text-primary hover:text-primary-600"> info@offertio.dk</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">9. Sikkerhed</h2>
              <p className="text-gray-700">
                Vi beskytter dine oplysninger med passende tekniske og organisatoriske foranstaltninger for at forhindre 
                uautoriseret adgang, tab eller ødelæggelse.
              </p>
            </section>
          </div>
        ) : (
          <div className="space-y-6 text-gray-700">
            <h1 className="text-3xl font-bold text-black mb-6">Privacy Policy</h1>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">1. Introduction</h2>
              <p>
                This Privacy Policy explains how Offertio ("we", "us", "our") collects, uses, and shares your personal information
                when you use our platform at offertio.dk ("the Platform").
              </p>
              <p>
                We are committed to protecting your personal data and ensuring its privacy, security, and integrity. This Privacy
                Policy outlines our practices regarding the collection, use, and disclosure of your information when you use our
                service, and informs you about your privacy rights.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">2. Information We Collect</h2>
              <h3 className="text-lg font-semibold text-primary-600 mb-2">2.1 Personal Information</h3>
              <p>
                We collect information that you provide directly to us when registering, creating or modifying your account, setting
                up preferences, or interacting with our platform:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Contact information (name, email address, phone number, company name)</li>
                <li>Account credentials (username, password)</li>
                <li>Profile information (job title, company size, industry)</li>
                <li>Billing and payment details</li>
                <li>Tender preferences and selection criteria</li>
                <li>Documents you upload to our platform</li>
              </ul>

              <h3 className="text-lg font-semibold text-primary-600 mb-2">2.2 Usage Information</h3>
              <p>
                We automatically collect certain information about your use of the Platform:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Log and usage data (IP address, browser type, access time, pages viewed)</li>
                <li>Device information (operating system, browser type, device type)</li>
                <li>Engagement metrics (features used, tenders viewed, notifications received)</li>
              </ul>

              <h3 className="text-lg font-semibold text-primary-600 mb-2">2.3 Cookies and Similar Technologies</h3>
              <p>
                We use cookies and similar tracking technologies to track activity on our Platform and to maintain certain information.
                Cookies are files with a small amount of data which may include an anonymous unique identifier.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">3. How We Use Your Information</h2>
              <p>We use the information we collect for various purposes, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Providing and maintaining our Platform</li>
                <li>Personalizing your experience</li>
                <li>Processing and completing transactions</li>
                <li>Matching you with relevant tender opportunities</li>
                <li>Sending notifications about tender opportunities and deadlines</li>
                <li>Providing customer support</li>
                <li>Improving and optimizing our Platform</li>
                <li>Monitoring usage of our Platform</li>
                <li>Detecting, preventing, and addressing technical issues</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">4. Sharing Your Information</h2>
              <p>We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Providers:</strong> Third-party companies that perform services on our behalf, such as payment processing, data analysis, email delivery, hosting services, and customer service.</li>
                <li><strong>Business Partners:</strong> We may share information with our business partners to offer certain products, services, or promotions.</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information where required by law or to protect our rights.</li>
                <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition, your data may be transferred.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">5. Data Retention</h2>
              <p>
                We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy.
                We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes,
                and enforce our policies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">6. Data Security</h2>
              <p>
                We implement a variety of security measures to maintain the safety of your personal information. Your personal information
                is contained behind secured networks and is only accessible by a limited number of persons who have special access rights
                and are required to keep the information confidential.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">7. Your Data Protection Rights</h2>
              <p>Under the GDPR and similar regulations, you have certain rights regarding your personal data, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Right to Access:</strong> You have the right to request copies of your personal data.</li>
                <li><strong>Right to Rectification:</strong> You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.</li>
                <li><strong>Right to Erasure:</strong> You have the right to request that we erase your personal data, under certain conditions.</li>
                <li><strong>Right to Restrict Processing:</strong> You have the right to request that we restrict the processing of your personal data, under certain conditions.</li>
                <li><strong>Right to Object to Processing:</strong> You have the right to object to our processing of your personal data, under certain conditions.</li>
                <li><strong>Right to Data Portability:</strong> You have the right to request that we transfer the data we have collected to another organization, or directly to you, under certain conditions.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">8. Children's Privacy</h2>
              <p>
                Our Platform is not intended for children under the age of 18. We do not knowingly collect personally identifiable
                information from children under 18. If you are a parent or guardian and you are aware that your child has provided
                us with personal data, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">9. Changes to This Privacy Policy</h2>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy
                on this page and updating the "last updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-black mt-6 mb-3">10. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <p>
                Email: info@offertio.dk<br />
                Phone: +45 28 91 88 72<br />
                Address: Offertio ApS, Copenhagen, Denmark
              </p>
            </section>
          </div>
        )}
      </div>
    </LegalPageLayout>
  );
};

export default PrivacyPolicy;
